import { getValidAccessToken } from '../oauth-browser';
import { AUTH_ADMIN_TOKEN_KEY } from '../../auth/auth-storage';
import type { UpdateScreenInput } from '../update-screen-input';
import type { MediaPayload } from '../../content/screens/media-payload';
import type { RemoveMediaPayload } from '../../content/screens/remove-media-payload';
import type { ScreenResult } from '../screen-result';
import type { SignageLibraryItem } from './signage-library-item';
import type { MediaUploadTicket } from './media-upload-ticket';
import type { ScreenCommand, ScreenCommandResult } from './screen-command';

// Get the signage API base path depending on platform
function getSignageApiUrl(organizationId: number): string {
  return `https://api.trinnvis.io/organizations/${organizationId}/signage`;
}

// Types for API responses
interface Screen {
  displayId: number;
  name: string;
  playerConnected?: boolean;
  /** 8-tegns kode for paret spiller (BLE-navn = `Trinnvis-<playerCode>`); null/utelatt når ingen spiller er paret. */
  playerCode?: string;
  orderStatus?: 'TRIAL' | 'ORDERED' | 'DELIVERED';
  screenshotUrl?: string;
}

interface CreateScreenResponse {
  displayId: number;
  name: string;
}

// Bygg hele Authorization-headeren. Admin-innlogging «på vegne av bruker» (go-
// endepunktet) bruker en Basic-token i localStorage, ikke en OAuth-token — uten
// dette feilet alle signage-kall for admin-sesjoner (#13136). Speiler mønsteret
// i substance-upload-processor.ts / d-app.ts fra #12431.
//
// Admin-innlogging er kun web, så admin-token leses alltid fra localStorage.
// OAuth-token bruker SecureStorage på native (håndtert av getValidAccessToken).
async function getAuthorizationHeader(): Promise<string> {
  const adminToken = localStorage.getItem(AUTH_ADMIN_TOKEN_KEY);
  if (adminToken) {
    return `Basic ${adminToken}`;
  }

  const currentAccessToken = await getValidAccessToken();
  // Forhindrer at vi sender "Authorization: Bearer " (tom token) til API-en, som
  // gir en stille 401 uten at brukeren forstår at sesjonen er utløpt (#12911).
  if (!currentAccessToken) {
    throw new Error('No valid access token — user must re-authenticate');
  }
  return `Bearer ${currentAccessToken}`;
}

// Create a new screen
export async function createScreen(organizationId: number, screenName: string): Promise<CreateScreenResponse> {
  console.log(`[SignageAPI] Creating screen - OrgID: ${organizationId}, Name: ${screenName}`);

  try {
    const authorization = await getAuthorizationHeader();
    console.log(`[SignageAPI] Got auth token for create screen`);

    const requestBody = {
      screenName: String(screenName).trim(),
    };
    console.log(`[SignageAPI] Request body:`, JSON.stringify(requestBody));

    const response = await fetch(`${getSignageApiUrl(organizationId)}/screens`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: authorization,
      },
      body: JSON.stringify(requestBody),
    });

    console.log(`[SignageAPI] Create screen response status: ${response.status}`);

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`[SignageAPI] Create screen error response: ${errorText}`);

      let errorMessage = `Failed to create screen: ${response.statusText}`;
      try {
        const errorJson = JSON.parse(errorText);
        errorMessage = errorJson.error || errorMessage;
      } catch (_e) {
        // If error text is not JSON, use the raw text
        if (errorText) {
          errorMessage = errorText;
        }
      }

      throw new Error(errorMessage);
    }

    const result = await response.json();
    console.log('[SignageAPI] Screen created successfully:', result);
    return result;
  } catch (error) {
    console.error('[SignageAPI] Error in createScreen:', error);
    throw error;
  }
}

// Get all screens for an organization
export async function getScreensForOrganization(organizationId: number): Promise<Screen[]> {
  const authorization = await getAuthorizationHeader();

  const response = await fetch(`${getSignageApiUrl(organizationId)}/screens`, {
    method: 'GET',
    headers: {
      Authorization: authorization,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `Failed to fetch screens: ${response.statusText}`);
  }

  return response.json();
}

// Fetch the merged library (standard items + org uploads) for an organization
export async function fetchLibrary(organizationId: number): Promise<SignageLibraryItem[]> {
  const authorization = await getAuthorizationHeader();
  const response = await fetch(`${getSignageApiUrl(organizationId)}/library`, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      Authorization: authorization,
    },
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch signage library: ${response.status} ${response.statusText}`);
  }
  return (await response.json()) as SignageLibraryItem[];
}

// Fetch screen with legacy format (migrated from fetch-screen.ts)
export async function fetchScreen(id: number, organizationId: number): Promise<ScreenResult> {
  console.log(`[SignageAPI] Fetching screen - ID: ${id}, OrgID: ${organizationId}`);

  try {
    const authorization = await getAuthorizationHeader();
    console.log(`[SignageAPI] Got auth token`);

    const url = `${getSignageApiUrl(organizationId)}/screen?screenId=${id}`;
    console.log(`[SignageAPI] Fetching from URL: ${url}`);

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: authorization,
      },
    });

    console.log(`[SignageAPI] Response status: ${response.status}`);

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`[SignageAPI] Error response: ${errorText}`);
      throw new Error(`HTTP error! Status: ${response.status}, Message: ${errorText}`);
    }

    const data: unknown = await response.json();
    // console.log('[SignageAPI] Screen data received:', JSON.stringify(data, null, 2));

    return data as ScreenResult;
  } catch (error) {
    console.error('[SignageAPI] Error in fetchScreen:', error);
    throw error;
  }
}

/**
 * Vent til en nyopprettet skjerm er spørrbar via GET /screen.
 *
 * POST /screens returnerer `displayId` før skjermdetaljen er konsistent på
 * serveren, så en umiddelbar `fetchScreen` (både i `reloadScreens` og i
 * `buildScreenView` ved navigering) 404'er rett etter opprettelse. Vi poller
 * `fetchScreen` til den svarer 2xx, slik at navigeringen ikke lander på en 404.
 *
 * Svelger feil og returnerer når forsøkene er brukt opp — kalleren navigerer
 * uansett, og `buildScreenView` gir da en ekte feil hvis skjermen fortsatt
 * mangler. Standard: ~5 s (10 forsøk × 500 ms) som dekker konsistens-vinduet.
 */
export async function waitForScreenReady(
  organizationId: number,
  screenId: number,
  { attempts = 10, delayMs = 500 }: { attempts?: number; delayMs?: number } = {},
): Promise<void> {
  for (let attempt = 0; attempt < attempts; attempt++) {
    try {
      await fetchScreen(screenId, organizationId);
      return;
    } catch (error) {
      if (attempt === attempts - 1) {
        console.warn(`[SignageAPI] waitForScreenReady ga opp for skjerm ${screenId} etter ${attempts} forsøk`, error);
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
  }
}

// Update screen (migrated from update-screen.ts)
export async function updateScreen(input: UpdateScreenInput): Promise<void> {
  const authorization = await getAuthorizationHeader();

  // Extract organizationId from input and create payload without it
  const { organizationId, ...payload } = input;

  const response = await fetch(`${getSignageApiUrl(organizationId)}/screen`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: authorization,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! Status: ${response.status}`);
  }

  const data: unknown = await response.json();
  console.log(JSON.stringify(data, null, 2));
}

// Add media to screen (migrated from add-screen-media.ts)
export async function addScreenMedia(input: MediaPayload): Promise<void> {
  const authorization = await getAuthorizationHeader();

  // Extract organizationId from input and create payload without it
  const { organizationId, screenId, ...payload } = input;

  const response = await fetch(`${getSignageApiUrl(organizationId)}/media`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: authorization,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! Status: ${response.status}`);
  }

  const data: unknown = await response.json();
  console.log(JSON.stringify(data, null, 2));
}

/**
 * Step 1 of direct-to-S3 upload: reserve a media id and a presigned PUT URL the file is uploaded to.
 */
export async function createMediaUploadUrl(
  organizationId: number,
  name: string,
  mimeType: string,
): Promise<MediaUploadTicket> {
  const authorization = await getAuthorizationHeader();
  const response = await fetch(`${getSignageApiUrl(organizationId)}/media/upload-url`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: authorization,
    },
    body: JSON.stringify({ name, mimeType }),
  });
  if (!response.ok) {
    throw new Error(`HTTP error! Status: ${response.status}`);
  }
  return (await response.json()) as MediaUploadTicket;
}

/**
 * Step 3 of direct-to-S3 upload: the file has been PUT to S3; ask the server to validate and
 * finalize it (image fast path or conversion).
 */
export async function completeMediaUpload(
  organizationId: number,
  mediaId: string,
  name: string,
  mimeType: string,
): Promise<void> {
  const authorization = await getAuthorizationHeader();
  const response = await fetch(`${getSignageApiUrl(organizationId)}/media/${encodeURIComponent(mediaId)}/complete`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: authorization,
    },
    body: JSON.stringify({ name, mimeType }),
  });
  if (!response.ok) {
    throw new Error(`HTTP error! Status: ${response.status}`);
  }
}

/**
 * Upload a media file directly to S3 via a presigned URL (#13237 / dabih-api#4637):
 *   1. reserve a presigned PUT URL,
 *   2. PUT the raw file straight to S3 (no bytes pass through the API),
 *   3. commit/finalize so the server validates and processes it.
 * Replaces the old base64-in-JSON path that failed for large files (#13236). Returns the mediaId.
 */
export async function uploadScreenMedia(input: {
  organizationId: number;
  file: File;
  name: string;
  mimeType: string;
}): Promise<string> {
  const { organizationId, file, name, mimeType } = input;

  const ticket = await createMediaUploadUrl(organizationId, name, mimeType);

  // PUT straight to S3. The presigned URL carries auth, so send only the Content-Type the URL was
  // signed with (returned in the ticket) — a mismatch fails the signature.
  const putResponse = await fetch(ticket.uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': ticket.contentType },
    body: file,
  });
  if (!putResponse.ok) {
    throw new Error(`S3 upload failed! Status: ${putResponse.status}`);
  }

  await completeMediaUpload(organizationId, ticket.mediaId, name, mimeType);
  return ticket.mediaId;
}

/**
 * Push redigerings-tilstand til skjermens draft-lokasjon på serveren.
 * Brukes til live forhåndsvisning under redigering — server svarer på
 * `GET /signage/preview/{token}/settings?draft=true` med denne payloaden,
 * og `mode=draft`-renderen poller hvert 60. sekund (postMessage gir ~0 ms latency).
 *
 * Krever autentisert sesjon med skrivetilgang til skjermens organisasjon.
 * Backend rydder draft automatisk ved vellykket save.
 */
export async function updateScreenDraft(previewToken: string, settings: unknown): Promise<void> {
  const authorization = await getAuthorizationHeader();
  const response = await fetch(`https://api.trinnvis.io/signage/preview/${encodeURIComponent(previewToken)}/draft`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: authorization,
    },
    body: JSON.stringify(settings),
  });
  if (!response.ok) {
    throw new Error(`updateScreenDraft: HTTP ${response.status}`);
  }
}

/**
 * Slett draft-lokasjonen for en skjerm. Kalles ved «Avbryt» i editoren,
 * så preview-iframen faller tilbake til lagret tilstand neste gang den
 * lastes på nytt.
 */
export async function deleteScreenDraft(previewToken: string): Promise<void> {
  const authorization = await getAuthorizationHeader();
  const response = await fetch(`https://api.trinnvis.io/signage/preview/${encodeURIComponent(previewToken)}/draft`, {
    method: 'DELETE',
    headers: {
      Authorization: authorization,
    },
  });
  if (!response.ok && response.status !== 404) {
    throw new Error(`deleteScreenDraft: HTTP ${response.status}`);
  }
}

// Remove media from screen (migrated from remove-screen-media.ts)
export async function removeScreenMedia(input: RemoveMediaPayload): Promise<void> {
  const authorization = await getAuthorizationHeader();

  // Extract organizationId from input and create payload without it
  const { organizationId, screenId, ...payload } = input;

  const response = await fetch(`${getSignageApiUrl(organizationId)}/media`, {
    method: 'DELETE',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: authorization,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! Status: ${response.status}`);
  }

  const data: unknown = await response.json();
  console.log(JSON.stringify(data, null, 2));
}

// Generate text image
export async function generateTextImage(input: {
  text: string;
  organizationId: number;
  name: string;
  backgroundColor?: string;
  textColor?: string;
  fontSize?: number;
  width?: number;
  height?: number;
  padding?: number;
}): Promise<{ success: boolean; imageUrl: string; name: string }> {
  const authorization = await getAuthorizationHeader();

  // Extract organizationId from input and create payload without it
  const { organizationId, ...payload } = input;

  const response = await fetch(`${getSignageApiUrl(organizationId)}/media/text-image`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: authorization,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! Status: ${response.status}`);
  }

  return response.json();
}

// Slett et library-item (slide eller asset). Brukes til "Mine lysbilder"-
// sletting i forslagspanelet samt asset-galleri-sletting når server eier
// kontrakten. Returnerer 200 ved suksess, 404 hvis itemId er ukjent for org-en.
// Server-semantikk når sliden er i bruk i en aktiv playlist er for tiden
// ikke definert — se trinnvis/dabih-web#12742.
export async function removeLibraryItem(organizationId: number, itemId: string): Promise<void> {
  const authorization = await getAuthorizationHeader();
  const url = `${getSignageApiUrl(organizationId)}/library/${encodeURIComponent(itemId)}`;
  const response = await fetch(url, {
    method: 'DELETE',
    headers: { Authorization: authorization },
  });
  if (!response.ok) {
    const errorText = await response.text().catch(() => '');
    throw new Error(`Failed to delete library item: ${response.status} ${response.statusText} ${errorText}`.trim());
  }
}

/**
 * Send en fjernstyringskommando til en skjerm (#13390 / dabih-api#4711).
 *
 * Endepunktet blokkerer synkront opptil `waitSeconds` (default 6, maks 15 på
 * server) og svarer så snart boksen har hentet kommandoen — eller når
 * tidsvinduet utløper (da er `delivered: false`).
 */
export async function sendScreenCommand(
  organizationId: number,
  screenId: number,
  command: ScreenCommand,
  waitSeconds = 6,
): Promise<ScreenCommandResult> {
  const authorization = await getAuthorizationHeader();

  // Endepunktet long-poller i inntil `waitSeconds` (serveren capper på 15 s).
  // Uten en abort kan en hengende socket (ikke serverens venting) låse
  // knappene på ubestemt tid – gi derfor litt margin over long-pollen.
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), (waitSeconds + 6) * 1000);

  let response: Response;
  try {
    response = await fetch(
      `${getSignageApiUrl(organizationId)}/screen/${screenId}/command?waitSeconds=${waitSeconds}`,
      {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: authorization,
        },
        body: JSON.stringify({ command }),
        signal: controller.signal,
      },
    );
  } finally {
    clearTimeout(timeout);
  }

  if (!response.ok) {
    const errorText = await response.text().catch(() => '');
    let errorMessage = `Failed to send screen command: ${response.statusText}`;
    try {
      const errorJson = JSON.parse(errorText);
      errorMessage = errorJson.error || errorMessage;
    } catch (_e) {
      if (errorText) {
        errorMessage = errorText;
      }
    }
    throw new Error(errorMessage);
  }

  return (await response.json()) as ScreenCommandResult;
}

// Pair a screen with a player using the 8-character code
export async function pairScreen(
  organizationId: number,
  screenId: number,
  playerCode: string,
): Promise<{ success: boolean }> {
  const authorization = await getAuthorizationHeader();

  const response = await fetch(`${getSignageApiUrl(organizationId)}/screen/pair`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: authorization,
    },
    body: JSON.stringify({ screenId, playerCode }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage = `Failed to pair screen: ${response.statusText}`;
    try {
      const errorJson = JSON.parse(errorText);
      errorMessage = errorJson.error || errorMessage;
    } catch (_e) {
      if (errorText) {
        errorMessage = errorText;
      }
    }
    throw new Error(errorMessage);
  }

  return response.json();
}

// Bestill mediespiller for en skjerm (TRIAL → ORDERED). Erstatter den tidligere
// flyten der web mutert navnet med [A]-prefiks + pairScreen(generateOrderCode()).
// Player-registrering (ORDERED → DELIVERED) er nå en intern admin-oppgave.
// dabih-api #4590/#4591.
export async function orderScreen(
  organizationId: number,
  screenId: number,
): Promise<{ success: boolean; orderStatus: string }> {
  const authorization = await getAuthorizationHeader();

  const response = await fetch(`${getSignageApiUrl(organizationId)}/screen/${screenId}/order`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: authorization,
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage = `Failed to order screen: ${response.statusText}`;
    try {
      const errorJson = JSON.parse(errorText);
      errorMessage = errorJson.error || errorMessage;
    } catch (_e) {
      if (errorText) {
        errorMessage = errorText;
      }
    }
    throw new Error(errorMessage);
  }

  return response.json();
}

// Delete a screen
export async function deleteScreen(organizationId: number, screenId: number): Promise<{ success: boolean }> {
  const authorization = await getAuthorizationHeader();

  const response = await fetch(`${getSignageApiUrl(organizationId)}/screen?screenId=${screenId}`, {
    method: 'DELETE',
    headers: {
      Authorization: authorization,
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage = `Failed to delete screen: ${response.statusText}`;
    try {
      const errorJson = JSON.parse(errorText);
      errorMessage = errorJson.error || errorMessage;
    } catch (_e) {
      if (errorText) {
        errorMessage = errorText;
      }
    }
    throw new Error(errorMessage);
  }

  return response.json();
}

// Unpair a screen from its player
export async function unpairScreen(organizationId: number, screenId: number): Promise<{ success: boolean }> {
  const authorization = await getAuthorizationHeader();

  const response = await fetch(`${getSignageApiUrl(organizationId)}/screen/unpair`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: authorization,
    },
    body: JSON.stringify({ screenId }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage = `Failed to unpair screen: ${response.statusText}`;
    try {
      const errorJson = JSON.parse(errorText);
      errorMessage = errorJson.error || errorMessage;
    } catch (_e) {
      if (errorText) {
        errorMessage = errorText;
      }
    }
    throw new Error(errorMessage);
  }

  return response.json();
}
